/**
 * Ad-hoc capture — for field techs who aren't working a sampling event.
 * Pick a site, pick a well, enter a gauging reading. Submits to
 * /api/v1/data-vault/intake/field-entry (the loose-data intake path,
 * with idempotency from Phase 2.9a) rather than the event-bound
 * /sampling/events/{id}/field-records.
 *
 * Sites + wells come from whatever the bootstrap cached — for an admin/
 * analyst that's every site in the tenant; for a field_tech it's their
 * assigned sites. Either way, no sampling plan is required.
 *
 * Three steps in one page:
 *   1. site dropdown   (cached_sites)
 *   2. well dropdown   (cached_wells for the chosen site)
 *   3. gauging form    (DTW + water elev + product thickness + is_dry +
 *                       notes + GPS + photos), submit
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { validateRowOffline, type ValidationIssue } from '@argos/validation';
import { ArrowLeftIcon, GpsIcon } from '../components/icons';
import { getDb, type CachedWell, type PhotoBlobRow } from '../db';
import { gpsAccuracyBucket, haversineMeters, useGpsFix } from '../hooks/useGpsFix';
import {
  capturePhoto,
  listPhotos,
  reassignPhotos,
  removePhoto,
  MAX_PHOTOS_PER_RECORD,
} from '../photos/capture';
import { enqueueSubmission } from '../sync/queue';
import * as haptics from '../lib/haptics';

interface SiteOption {
  site_id: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  geofence_radius_m?: number | null;
}

interface WellOption {
  well_name: string;
  normalized_name: string;
  latitude?: number | null;
  longitude?: number | null;
  total_depth_ft?: number | null;
  casing_elevation_ft?: number | null;
  measuring_point_elevation_ft?: number | null;
  screen_top_ft?: number | null;
  geofence_radius_m?: number | null;
}

interface FormState {
  is_dry: boolean;
  depth_to_water: string;
  water_elevation: string;
  product_thickness: string;
  depth_to_product: string;
  casing_elevation: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  is_dry: false,
  depth_to_water: '',
  water_elevation: '',
  product_thickness: '',
  depth_to_product: '',
  casing_elevation: '',
  notes: '',
};

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function newUuid(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function AdHocCapturePage() {
  const navigate = useNavigate();
  const gps = useGpsFix(true);

  const [sites, setSites] = useState<SiteOption[] | null>(null);
  const [siteId, setSiteId] = useState<string>('');
  const [wells, setWells] = useState<WellOption[]>([]);
  const [wellName, setWellName] = useState<string>('');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submissionId] = useState<string>(() => newUuid());
  const [photos, setPhotos] = useState<PhotoBlobRow[]>([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load cached sites.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getDb().cached_sites.toArray();
        if (cancelled) return;
        const opts: SiteOption[] = rows.map((r) => {
          const d = r.data as {
            name?: string;
            latitude?: number | null;
            longitude?: number | null;
            geofence_radius_m?: number | null;
          };
          return {
            site_id: r.site_id,
            name: d.name ?? r.site_id.slice(0, 8),
            latitude: d.latitude,
            longitude: d.longitude,
            geofence_radius_m: d.geofence_radius_m,
          };
        });
        opts.sort((a, b) => a.name.localeCompare(b.name));
        setSites(opts);
      } catch (err) {
        setError((err as Error).message);
        setSites([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load wells for the chosen site.
  useEffect(() => {
    if (!siteId) {
      setWells([]);
      setWellName('');
      return;
    }
    let cancelled = false;
    void (async () => {
      const rows: CachedWell[] = await getDb()
        .cached_wells.where('site_id')
        .equals(siteId)
        .toArray();
      if (cancelled) return;
      const opts: WellOption[] = rows.map((r) => {
        const d = r.data as {
          name?: string;
          normalized_name?: string;
          latitude?: number | null;
          longitude?: number | null;
          total_depth_ft?: number | null;
          casing_elevation_ft?: number | null;
          measuring_point_elevation_ft?: number | null;
          screen_top_ft?: number | null;
          geofence_radius_m?: number | null;
        };
        return {
          well_name: r.well_name,
          normalized_name: d.normalized_name ?? r.well_name.trim().toLowerCase(),
          latitude: d.latitude,
          longitude: d.longitude,
          total_depth_ft: d.total_depth_ft,
          casing_elevation_ft: d.casing_elevation_ft,
          measuring_point_elevation_ft: d.measuring_point_elevation_ft,
          screen_top_ft: d.screen_top_ft,
          geofence_radius_m: d.geofence_radius_m,
        };
      });
      opts.sort((a, b) => a.well_name.localeCompare(b.well_name));
      setWells(opts);
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  // Load any photos already attached to this in-progress submission.
  useEffect(() => {
    let cancelled = false;
    void listPhotos(submissionId).then((rows) => {
      if (!cancelled) setPhotos(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  const selectedSite = sites?.find((s) => s.site_id === siteId) ?? null;
  const selectedWell = wells.find((w) => w.well_name === wellName) ?? null;

  const distanceToWell = useMemo(() => {
    if (!gps.fix || selectedWell?.latitude == null || selectedWell?.longitude == null)
      return null;
    return haversineMeters(gps.fix, {
      lat: selectedWell.latitude,
      lon: selectedWell.longitude,
    });
  }, [gps.fix, selectedWell]);

  const effectiveRadius =
    selectedWell?.geofence_radius_m ?? selectedSite?.geofence_radius_m ?? 30;
  const overGeofence = distanceToWell != null && distanceToWell > effectiveRadius;

  const validationIssues: ValidationIssue[] = useMemo(() => {
    if (!selectedWell) return [];
    const wmap = new Map<string, unknown>();
    wmap.set(selectedWell.normalized_name, {
      name: selectedWell.well_name,
      total_depth_ft: selectedWell.total_depth_ft ?? null,
      casing_elevation_ft: selectedWell.casing_elevation_ft ?? null,
      measuring_point_elevation_ft: selectedWell.measuring_point_elevation_ft ?? null,
      screen_top_ft: selectedWell.screen_top_ft ?? null,
    });
    return validateRowOffline(
      {
        well_name: selectedWell.well_name,
        depth_to_water_ft: form.depth_to_water,
        data_type: 'gauging',
      },
      { rules: {}, wells: wmap, analytes: new Map() },
    ).filter((i) => i.rule === 'SCREEN_INTERVAL_CHECK');
  }, [selectedWell, form.depth_to_water]);

  const accuracyBucket = gpsAccuracyBucket(gps.fix?.accuracy_m);

  const handleAddPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (photos.length >= MAX_PHOTOS_PER_RECORD) {
      setError(`Max ${MAX_PHOTOS_PER_RECORD} photos`);
      return;
    }
    setPhotoBusy(true);
    try {
      await capturePhoto(
        submissionId,
        `photo_${Date.now().toString(36)}`,
        file,
        gps.fix
          ? { lat: gps.fix.lat, lon: gps.fix.lon, accuracy_m: gps.fix.accuracy_m }
          : null,
      );
      setPhotos(await listPhotos(submissionId));
    } catch (err) {
      setError(`Photo capture failed: ${(err as Error).message}`);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleRemovePhoto = async (key: string) => {
    await removePhoto(key);
    setPhotos(await listPhotos(submissionId));
  };

  const canSubmit = !!siteId && !!wellName && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    haptics.tap();
    setSubmitting(true);
    setError(null);
    try {
      // Build the values dict using the keys the /intake/field-entry
      // backend expects for data_type=gauging:
      //   depth_to_water, water_elevation, casing_elevation,
      //   depth_to_product, product_thickness, is_dry, notes
      const values: Record<string, string> = {};
      if (form.is_dry) values.is_dry = 'true';
      if (form.depth_to_water.trim()) values.depth_to_water = form.depth_to_water.trim();
      if (form.water_elevation.trim()) values.water_elevation = form.water_elevation.trim();
      if (form.casing_elevation.trim()) values.casing_elevation = form.casing_elevation.trim();
      if (form.depth_to_product.trim()) values.depth_to_product = form.depth_to_product.trim();
      if (form.product_thickness.trim()) values.product_thickness = form.product_thickness.trim();

      const rowId = newUuid();
      const payload = {
        site_id: siteId,
        data_type: 'gauging',
        client_submission_id: submissionId,
        rows: [
          {
            well_name: wellName,
            sample_date: todayISO(),
            values,
            notes: form.notes.trim() || undefined,
            gps_latitude: gps.fix?.lat,
            gps_longitude: gps.fix?.lon,
            client_row_id: rowId,
          },
        ],
      };

      const id = await enqueueSubmission({
        endpoint: '/api/v1/data-vault/intake/field-entry',
        payload,
        event_id: null,
      });
      // Migrate photos onto the canonical submission id (enqueue
      // generates a fresh UUID).
      if (id !== submissionId) await reassignPhotos(submissionId, id);

      navigate('/events', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error && sites == null) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <p className="text-accent-red mb-4">{error}</p>
        <Link to="/events" className="btn-secondary">
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-6 gap-6">
      <Link
        to="/events"
        className="inline-flex items-center gap-1 text-mono-400 text-sm"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Events
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">Quick reading</h1>
        <p className="text-mono-400 text-sm">
          Capture a gauging reading without a sampling event.
        </p>
      </div>

      {/* Step 1: site */}
      <div>
        <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
          Site
        </label>
        <select
          className="input-field"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
        >
          <option value="">Select a site…</option>
          {(sites ?? []).map((s) => (
            <option key={s.site_id} value={s.site_id}>
              {s.name}
            </option>
          ))}
        </select>
        {sites != null && sites.length === 0 && (
          <p className="text-mono-500 text-xs mt-1">
            No sites cached. Refresh from the events page when online.
          </p>
        )}
      </div>

      {/* Step 2: well */}
      {siteId && (
        <div>
          <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
            Well
          </label>
          <select
            className="input-field"
            value={wellName}
            onChange={(e) => setWellName(e.target.value)}
          >
            <option value="">Select a well…</option>
            {wells.map((w) => (
              <option key={w.well_name} value={w.well_name}>
                {w.well_name}
              </option>
            ))}
          </select>
          {wells.length === 0 && (
            <p className="text-mono-500 text-xs mt-1">
              No wells cached for this site.
            </p>
          )}
        </div>
      )}

      {/* Step 3: form (only once a well is chosen) */}
      {wellName && (
        <>
          {/* GPS */}
          <section className="border border-mono-800 rounded-md p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 text-sm text-mono-300">
                <GpsIcon className="h-4 w-4" /> GPS fix
              </div>
              <span
                className={
                  accuracyBucket === 'good'
                    ? 'text-accent-green text-sm'
                    : accuracyBucket === 'ok'
                      ? 'text-accent-amber text-sm'
                      : 'text-accent-red text-sm'
                }
              >
                {gps.fix
                  ? `±${Math.round(gps.fix.accuracy_m)} m`
                  : gps.unavailable
                    ? 'unavailable'
                    : 'acquiring…'}
              </span>
            </div>
            {gps.fix && distanceToWell != null && (
              <p
                className={
                  overGeofence
                    ? 'text-accent-amber text-xs'
                    : 'text-accent-green text-xs'
                }
              >
                {Math.round(distanceToWell)} m from {wellName}
                {overGeofence && ` — outside ${effectiveRadius} m geofence`}
              </p>
            )}
          </section>

          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-mono-300 text-sm">Well is dry</span>
              <button
                type="button"
                role="switch"
                aria-checked={form.is_dry}
                onClick={() => setForm((f) => ({ ...f, is_dry: !f.is_dry }))}
                className={`min-h-touch min-w-[3.5rem] px-1 rounded-full transition-colors ${
                  form.is_dry ? 'bg-mono-100' : 'bg-mono-700'
                }`}
              >
                <span
                  className={`block h-5 w-5 rounded-full bg-mono-950 transition-transform ${
                    form.is_dry ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {!form.is_dry && (
              <TextNumberField
                label="Depth to water (ft)"
                value={form.depth_to_water}
                onChange={(v) => setForm((f) => ({ ...f, depth_to_water: v }))}
              />
            )}
            <TextNumberField
              label="Water elevation (ft MSL)"
              value={form.water_elevation}
              onChange={(v) => setForm((f) => ({ ...f, water_elevation: v }))}
            />
            <TextNumberField
              label="Casing elevation (ft MSL)"
              value={form.casing_elevation}
              onChange={(v) => setForm((f) => ({ ...f, casing_elevation: v }))}
            />
            <TextNumberField
              label="Depth to product (ft)"
              value={form.depth_to_product}
              onChange={(v) => setForm((f) => ({ ...f, depth_to_product: v }))}
            />
            <TextNumberField
              label="Product thickness (ft)"
              value={form.product_thickness}
              onChange={(v) => setForm((f) => ({ ...f, product_thickness: v }))}
            />

            <div>
              <label className="block text-xs uppercase tracking-wider text-mono-500 mb-2">
                Photos ({photos.length} / {MAX_PHOTOS_PER_RECORD})
              </label>
              {photos.length > 0 && (
                <ul className="grid grid-cols-3 gap-2 mb-2">
                  {photos.map((p) => (
                    <PhotoThumb
                      key={p.key}
                      photo={p}
                      onRemove={() => handleRemovePhoto(p.key)}
                    />
                  ))}
                </ul>
              )}
              <label
                className={`btn-secondary inline-block w-full text-center cursor-pointer ${
                  photos.length >= MAX_PHOTOS_PER_RECORD || photoBusy
                    ? 'pointer-events-none opacity-50'
                    : ''
                }`}
              >
                {photoBusy ? 'Compressing…' : '+ Add photo'}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleAddPhoto}
                  disabled={photos.length >= MAX_PHOTOS_PER_RECORD || photoBusy}
                />
              </label>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
                Notes
              </label>
              <textarea
                rows={3}
                className="input-field"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </section>

          {validationIssues.length > 0 && (
            <section className="space-y-2">
              {validationIssues.map((issue, i) => (
                <div
                  key={i}
                  className="text-xs border rounded p-2 border-amber-900/40 bg-amber-950/20 text-accent-amber"
                >
                  ⚠ {issue.message}
                </div>
              ))}
            </section>
          )}

          {error && (
            <div className="text-accent-red text-sm border border-red-900/40 rounded p-2 bg-red-950/20">
              {error}
            </div>
          )}
        </>
      )}

      {/* Sticky submit — keeps the action visible on long forms. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-safe-4 bg-mono-950/95 backdrop-blur border-t border-mono-800">
        <button
          type="button"
          className="btn-primary w-full"
          disabled={!canSubmit}
          onClick={submit}
        >
          {submitting ? 'Saving…' : 'Save reading'}
        </button>
        {wellName && (
          <p className="text-mono-500 text-xs text-center mt-2">
            Saves immediately to your device, syncs when online.
          </p>
        )}
      </div>
    </div>
  );
}

function TextNumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-mono-500 mb-1">
        {label}
      </label>
      <input
        type="text"
        inputMode="decimal"
        pattern="[0-9.]*"
        className="input-field text-lg"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function PhotoThumb({
  photo,
  onRemove,
}: {
  photo: PhotoBlobRow;
  onRemove: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const u = URL.createObjectURL(photo.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [photo.blob]);
  return (
    <li className="relative aspect-square overflow-hidden rounded border border-mono-800">
      {url && <img src={url} alt="Field capture" className="h-full w-full object-cover" />}
      <button
        type="button"
        onClick={onRemove}
        /* 44×44 per Apple HIG. */
        className="absolute top-1 right-1 h-11 w-11 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Remove photo"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3 6h18" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        </svg>
      </button>
    </li>
  );
}
